// src/components/UI.js
import React, { useState, useRef, useEffect } from "react";
import { Bot, ChevronDown } from "lucide-react";

/* ── Button ─────────────────────────────────────────────── */
export function Button({
  children, variant = "primary", size = "md",
  loading = false, className = "", style = {}, ...props
}) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    gap: 8, fontFamily: "inherit", fontWeight: 600,
    borderRadius: "var(--radius)", border: "none",
    cursor: loading || props.disabled ? "not-allowed" : "pointer",
    transition: "all .2s", whiteSpace: "nowrap",
    opacity: loading || props.disabled ? .6 : 1,
    letterSpacing: ".01em", position: "relative", overflow: "hidden",
  };
  const sizes = {
    sm: { padding: "7px 16px",  fontSize: 12, minHeight: 32 },
    md: { padding: "10px 22px", fontSize: 14, minHeight: 40 },
    lg: { padding: "13px 32px", fontSize: 15, minHeight: 48 },
  };
  const variants = {
  primary: {
      background: "var(--accent)",
      color: "#ffffff",
    },
    secondary: {
      background: "var(--surface-4)",
      color: "var(--text-1)",
      border: "1px solid var(--border-2)",
    },
    danger:    { background: "var(--red)",   color: "#fff" },
    ghost:     { background: "transparent",  color: "var(--text-2)", border: "1px solid var(--border-2)" },
    success:   {
      background: "var(--green)",
      color: "#fff",
    },
    dark:      { background: "var(--surface-5)", color: "var(--text-1)", border: "1px solid var(--border)" },
  };

  return (
    <button
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      className={`rb-btn ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && (
        <span style={{
          width: 14, height: 14,
          border: "2px solid currentColor", borderTopColor: "transparent",
          borderRadius: "50%", display: "inline-block",
          animation: "spin 1s linear infinite", flexShrink: 0,
        }}/>
      )}
      {children}
    </button>
  );
}

/* ── Input ──────────────────────────────────────────────── */
export function Input({ label, error, hint, icon: Icon, className = "", style = {}, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} className={className}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", letterSpacing: ".04em", textTransform: "uppercase" }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {Icon && (
          <span style={{
            position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)",
            color: "var(--text-3)", display: "flex", pointerEvents: "none",
          }}>
            <Icon size={15}/>
          </span>
        )}
        <input
          className="rb-input"
          style={{
            width: "100%",
            padding: Icon ? "11px 14px 11px 40px" : "11px 14px",
            border: `1px solid ${error ? "var(--red)" : "var(--border-2)"}`,
            borderRadius: "var(--radius)",
            fontSize: 14, color: "var(--text-1)",
            background: "var(--surface-4)",
            outline: "none",
            transition: "border-color .15s, box-shadow .15s",
            ...style,
          }}
          {...props}
        />
      </div>
      {error && <span style={{ fontSize: 12, color: "var(--red)" }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.5 }}>{hint}</span>}
    </div>
  );
}

/* ── Card ───────────────────────────────────────────────── */
export function Card({ children, style = {}, className = "", glow = false, interactive = false, ...props }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--border)",
        boxShadow: glow ? "var(--shadow-glow)" : "var(--shadow-sm)",
        ...style,
      }}
      className={`${interactive ? "rb-card-interactive" : ""} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── Section ────────────────────────────────────────────── */
// A titled card used for grouping related settings/controls. Defined here
// (not inline in whichever page uses it first) so any page can share the
// exact same look without duplicating the markup.
export function Section({ title, description, children }) {
  return (
    <Card style={{ padding: "28px 32px", marginBottom: 20 }}>
      <div style={{ marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700 }}>{title}</h2>
        {description && <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 4 }}>{description}</p>}
      </div>
      {children}
    </Card>
  );
}

/* ── Badge ──────────────────────────────────────────────── */
export function Badge({ children, color = "gray" }) {
  const colors = {
    gray:   { bg: "rgba(255,255,255,.07)", text: "var(--text-2)",   border: "var(--border-2)" },
    green:  { bg: "var(--green-lt)",       text: "#34d399",         border: "rgba(16,185,129,.3)" },
    red:    { bg: "var(--red-lt)",         text: "#f87171",         border: "rgba(239,68,68,.3)" },
    amber:  { bg: "var(--amber-lt)",       text: "#fbbf24",         border: "rgba(245,158,11,.3)" },
    blue:   { bg: "var(--accent-lt)",      text: "var(--accent)",   border: "var(--border-3)" },
    purple: { bg: "var(--purple-lt)",      text: "#a78bfa",         border: "rgba(124,58,237,.3)" },
    cyan:   { bg: "var(--accent-lt)",      text: "var(--accent)",   border: "var(--border-3)" },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 99,
      fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.text,
      border: `1px solid ${c.border}`,
      letterSpacing: ".03em",
    }}>
      {children}
    </span>
  );
}

/* ── Spinner ────────────────────────────────────────────── */
export function Spinner({ size = 20, color = "var(--accent)" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      border: `2px solid ${color}30`, borderTopColor: color,
      borderRadius: "50%", animation: "spin 1s linear infinite",
      flexShrink: 0,
    }}/>
  );
}

/* ── Skeletons ──────────────────────────────────────────── */
// Shown instead of an empty page or a lone spinner while data loads, so the
// layout is already in place and the page doesn't jump when content arrives.
export function Skeleton({ w = "100%", h = 14, r, style }) {
  return <span className="rb-skel" aria-hidden="true" style={{ display: "block", width: w, height: h, borderRadius: r, flexShrink: 0, ...style }} />;
}

export function ListSkeleton({ rows = 6, avatar = true }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg, 12px)" }}>
          {avatar && <Skeleton w={38} h={38} r={99} />}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            <Skeleton w={`${45 + ((i * 13) % 30)}%`} h={13} />
            <Skeleton w={`${25 + ((i * 17) % 25)}%`} h={11} />
          </div>
          <Skeleton w={64} h={22} r={99} />
        </div>
      ))}
    </div>
  );
}

// Whole-page placeholder: heading, a row of stat tiles, then list rows.
export function PageSkeleton({ stats = 4, rows = 5 }) {
  return (
    <div className="rb-page" style={{ padding: "36px 40px", maxWidth: 1100 }} role="status" aria-busy="true" aria-label="Loading">
      <Skeleton w={220} h={28} style={{ marginBottom: 10 }} />
      <Skeleton w={340} h={13} style={{ marginBottom: 28 }} />
      {stats > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {Array.from({ length: stats }).map((_, i) => (
            <div key={i} style={{ minWidth: 130, padding: "14px 20px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg, 12px)", display: "flex", flexDirection: "column", gap: 8 }}>
              <Skeleton w={48} h={22} /><Skeleton w={70} h={10} />
            </div>
          ))}
        </div>
      )}
      <ListSkeleton rows={rows} />
    </div>
  );
}

/* ── Modal ──────────────────────────────────────────────── */
export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,.75)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "var(--surface)",
        border: "1px solid var(--border-2)",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-lg)",
        width: "100%", maxWidth: width,
        maxHeight: "90vh", overflow: "auto",
        animation: "fadeIn .2s ease",
      }}>
        <div style={{
          padding: "24px 28px 0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, color: "var(--text-1)" }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "var(--surface-4)", border: "1px solid var(--border)",
              color: "var(--text-3)", cursor: "pointer",
              padding: "4px 8px", borderRadius: "var(--radius-sm)",
              fontSize: 16, lineHeight: 1, display: "flex",
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: "20px 28px 28px" }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Empty State ────────────────────────────────────────── */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div style={{
      textAlign: "center", padding: "60px 24px",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
    }}>
      {Icon && (
        <div style={{
          width: 60, height: 60,
          background: "var(--surface-4)",
          border: "1px solid var(--border)",
          borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--text-3)",
        }}>
          <Icon size={24}/>
        </div>
      )}
      <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)" }}>{title}</h3>
      {description && (
        <p style={{ fontSize: 14, color: "var(--text-3)", maxWidth: 320, lineHeight: 1.6 }}>{description}</p>
      )}
      {action}
    </div>
  );
}

/* ── Stat Card ──────────────────────────────────────────── */
export function StatCard({ label, value, icon: Icon, color = "cyan", delta }) {
  const colors = {
    cyan:   { text: "var(--accent)",  bg: "var(--accent-lt)",  border: "var(--border-3)" },
    green:  { text: "var(--green)",   bg: "var(--green-lt)",   border: "rgba(16,185,129,.2)" },
    amber:  { text: "var(--amber)",   bg: "var(--amber-lt)",   border: "rgba(245,158,11,.2)" },
    purple: { text: "#a78bfa",        bg: "var(--purple-lt)",  border: "rgba(124,58,237,.2)" },
  };
  const c = colors[color] || colors.cyan;
  return (
    <Card style={{ padding: "22px 24px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{
            fontSize: 11, fontWeight: 600, color: "var(--text-3)",
            textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8,
          }}>
            {label}
          </p>
          <p style={{
            fontSize: 32, fontWeight: 700,
            fontFamily: "var(--font-display)",
            color: "var(--text-1)", lineHeight: 1,
          }}>
            {value}
          </p>
          {delta !== undefined && (
            <p style={{
              fontSize: 12, fontWeight: 600,
              color: delta >= 0 ? "var(--green)" : "var(--red)",
              marginTop: 6,
            }}>
              {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}% this month
            </p>
          )}
        </div>
        {Icon && (
          <div style={{
            width: 46, height: 46,
            background: c.bg,
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: c.text, flexShrink: 0,
          }}>
            <Icon size={20}/>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── Zilo agent avatar + on-duty badge ──────────────────── */
// Shared everywhere the AI agent's identity should be visible to a
// business owner (Campaigns, Reviews, Dashboard, Settings) so it reads as
// "your assistant, on the job" rather than a faceless "AI" feature.
const ZILO_PHOTO_URL = "https://techbugz.com/wp-content/uploads/2026/02/Zilo-AI.png";

export function ZiloAvatar({ size = 56, online = true }) {
  const [imgError, setImgError] = useState(false);
  const ringPad = Math.max(2, Math.round(size * 0.05));
  const gapPad  = Math.max(1, Math.round(size * 0.022));
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {/* Brand gradient ring — a soft blue frame around the photo, with a matching glow */}
      <div style={{
        width: size, height: size, borderRadius: "50%", padding: ringPad,
        background: "linear-gradient(135deg, var(--accent), var(--accent-dim))",
        boxShadow: "0 6px 20px var(--accent-glow), 0 2px 8px rgba(0,0,0,.15)",
      }}>
        <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: "var(--surface)", padding: gapPad }}>
          {!imgError ? (
            <img
              src={ZILO_PHOTO_URL}
              alt="Zilo"
              onError={() => setImgError(true)}
              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%", borderRadius: "50%",
              background: "linear-gradient(135deg, var(--accent), var(--accent-dim))",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Bot size={Math.round(size * 0.5)} color="#fff" />
            </div>
          )}
        </div>
      </div>
      {online && (
        <span style={{
          position: "absolute", bottom: -1, right: -1,
          width: Math.max(13, Math.round(size * 0.28)),
          height: Math.max(13, Math.round(size * 0.28)),
          borderRadius: "50%", background: "var(--green)",
          border: "2.5px solid var(--surface)",
          animation: "pulse 2s ease infinite",
        }} />
      )}
    </div>
  );
}

export function ZiloOnDutyBadge({ name = "Zilo", subtitle = "On duty", size = 56, style = {} }) {
  return (
    <div style={{
      position: "relative", overflow: "hidden",
      display: "inline-flex", alignItems: "center", gap: 14,
      padding: "8px 22px 8px 8px", borderRadius: 99,
      background: "var(--accent-lt)",
      border: "1px solid var(--accent-border)",
      ...style,
    }}>
      <ZiloAvatar size={size} />
      <div style={{ lineHeight: 1.3 }}>
        <p style={{ fontSize: 17, fontWeight: 800, color: "var(--text-1)", fontFamily: "var(--font-display)" }}>{name}</p>
        <p style={{
          display: "flex", alignItems: "center", gap: 5,
          fontSize: 11, fontWeight: 700, color: "var(--green)",
          textTransform: "uppercase", letterSpacing: ".05em",
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", flexShrink: 0 }} />
          {subtitle}
        </p>
      </div>
    </div>
  );
}

/* ── Select ─────────────────────────────────────────────── */
// A themed dropdown to replace native <select> — the browser draws a
// native select's open list itself, so it can't be restyled to match
// dark mode or the rest of the UI. This one renders its own list.
// `options` accepts plain values (["a","b"]) or [{ value, label }].
export function Select({ value, onChange, options = [], placeholder = "Select…", size = "md", disabled = false, style = {} }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const norm = options.map(o => (o !== null && typeof o === "object" && "value" in o) ? o : { value: o, label: String(o) });
  const current = norm.find(o => String(o.value) === String(value));
  const isSm = size === "sm";

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", ...style }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", padding: isSm ? "6px 10px" : "9px 14px",
          border: `1px solid ${open ? "var(--accent)" : "var(--border-2)"}`,
          borderRadius: "var(--radius)", background: "var(--surface-4)", color: "var(--text-1)",
          fontSize: isSm ? 12.5 : 13.5, fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: "var(--font-body)", whiteSpace: "nowrap", opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{current ? current.label : placeholder}</span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: "var(--text-3)", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%", zIndex: 200,
          background: "var(--surface)", border: "1px solid var(--border-2)", borderRadius: "var(--radius)",
          boxShadow: "var(--shadow-lg)", maxHeight: 280, overflowY: "auto", padding: 4,
        }}>
          {norm.map(o => {
            const active = String(o.value) === String(value);
            return (
              <button
                key={o.value} type="button"
                disabled={!!o.disabled}
                title={o.hint || undefined}
                onClick={() => { if (o.disabled) return; onChange(o.value); setOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
                  borderRadius: "var(--radius-sm)", border: "none", cursor: o.disabled ? "not-allowed" : "pointer",
                  opacity: o.disabled ? 0.45 : 1,
                  background: active ? "var(--accent-lt)" : "transparent",
                  color: active ? "var(--accent)" : "var(--text-1)",
                  fontSize: 13, fontWeight: active ? 700 : 500, whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { if (!active && !o.disabled) e.currentTarget.style.background = "var(--surface-2)"; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}